import { useCallback, useEffect, useState } from "react";
import { Check, Copy, Download, Save, Sparkles } from "lucide-react";
import {
	artifactExtension,
	isPreviewable,
	useArtifactStore,
	wrapForVault,
} from "../../../../stores/artifact";
import { Modal } from "../../../common/Modal";
import type { DockKindProps } from "../../registry";
import { artifactData } from "./data";
import styles from "./ArtifactToolbar.module.css";

/**
 * The artifact card's chrome controls (2026-07-15) — Save-to-vault, Copy,
 * Download, and the auto-open preference. Lifted out of ArtifactCanvas's header
 * when the canvas became a dock kind.
 *
 * These are ACTIONS ON THE ARTIFACT, which is why they sit in the card's chrome
 * while Source↔Preview and the version switcher (statements about how you're
 * viewing it) live in the panel body's own header.
 */

interface KbOption {
	slug: string;
	title: string;
}

export function ArtifactToolbar({ panel }: DockKindProps) {
	const { language, content } = artifactData(panel);
	const autoOpen = useArtifactStore((s) => s.autoOpen);
	const setAutoOpen = useArtifactStore((s) => s.setAutoOpen);
	const [copied, setCopied] = useState(false);

	const [saveOpen, setSaveOpen] = useState(false);
	const [kbs, setKbs] = useState<KbOption[]>([]);
	const [kbsLoaded, setKbsLoaded] = useState(false);
	const [saveKb, setSaveKb] = useState("");
	const [saveTitle, setSaveTitle] = useState("");
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState("");
	const [savedSlug, setSavedSlug] = useState<string | null>(null);

	// Switching to a different artifact clears the transient save state so the
	// "Saved ✓" chip doesn't linger across an unrelated document.
	useEffect(() => {
		setSavedSlug(null);
		setSaveError("");
		setSaveOpen(false);
	}, [panel.id]);

	const openSaveDialog = useCallback(async () => {
		setSaveError("");
		setSavedSlug(null);
		setSaveTitle(panel.title);
		setSaveOpen(true);
		if (kbsLoaded) return;
		try {
			const res = await fetch("/api/kb");
			if (res.ok) {
				const list = (await res.json()) as KbOption[];
				const opts = (Array.isArray(list) ? list : []).map((k) => ({
					slug: k.slug,
					title: (k.title || k.slug).replace(/ — Index$/, ""),
				}));
				setKbs(opts);
				const first = opts[0];
				if (first) setSaveKb((cur) => cur || first.slug);
			}
		} catch {
			/* leave kbs empty → the dialog shows the "no KB" hint */
		} finally {
			setKbsLoaded(true);
		}
	}, [panel.title, kbsLoaded]);

	const doSave = useCallback(async () => {
		if (!saveKb || !saveTitle.trim() || saving) return;
		setSaving(true);
		setSaveError("");
		try {
			const res = await fetch("/api/wiki/article", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					kb_slug: saveKb,
					title: saveTitle.trim(),
					content: wrapForVault(content, language),
					entity_type: "document",
				}),
			});
			if (res.ok) {
				const data = (await res.json()) as { slug?: string };
				setSavedSlug(data.slug ?? "");
				setSaveOpen(false);
			} else {
				const err = await res.json().catch(() => ({ detail: res.statusText }));
				setSaveError(err.detail || "Failed to save");
			}
		} catch {
			setSaveError("Could not reach the server");
		} finally {
			setSaving(false);
		}
	}, [saveKb, saveTitle, saving, content, language]);

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(content);
			setCopied(true);
			setTimeout(() => setCopied(false), 1400);
		} catch {
			/* clipboard blocked — silent */
		}
	};

	const handleDownload = () => {
		const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `artifact.${artifactExtension(language)}`;
		document.body.appendChild(a);
		a.click();
		a.remove();
		URL.revokeObjectURL(url);
	};

	return (
		<>
			<button
				type="button"
				className={`${styles.iconBtn} ${autoOpen ? styles.iconActive : ""}`}
				onClick={() => setAutoOpen(!autoOpen)}
				title={
					autoOpen
						? "Auto-open artifacts is ON — click to turn off"
						: "Auto-open artifacts is OFF — click to turn on"
				}
				aria-label="Toggle auto-open artifacts"
				aria-pressed={autoOpen}
			>
				<Sparkles size={14} />
			</button>
			{savedSlug !== null ? (
				<a
					className={styles.savedChip}
					href={`/wiki?article=${encodeURIComponent(savedSlug)}`}
					title="Open the saved article"
				>
					<Check size={12} />
					<span>Saved</span>
				</a>
			) : (
				<button
					type="button"
					className={styles.iconBtn}
					onClick={openSaveDialog}
					title="Save to vault"
					aria-label="Save artifact to vault"
				>
					<Save size={14} />
				</button>
			)}
			<button
				type="button"
				className={styles.iconBtn}
				onClick={handleCopy}
				title={copied ? "Copied" : "Copy"}
				aria-label="Copy artifact"
			>
				{copied ? <Check size={14} /> : <Copy size={14} />}
			</button>
			<button
				type="button"
				className={styles.iconBtn}
				onClick={handleDownload}
				title="Download"
				aria-label="Download artifact"
			>
				<Download size={14} />
			</button>

			<Modal
				open={saveOpen}
				onClose={() => setSaveOpen(false)}
				title="Save to vault"
				maxWidth="440px"
				footer={
					<>
						<button
							type="button"
							className={styles.modalCancel}
							onClick={() => setSaveOpen(false)}
						>
							Cancel
						</button>
						<button
							type="button"
							className={styles.modalSave}
							onClick={doSave}
							disabled={saving || !saveKb || !saveTitle.trim()}
						>
							{saving ? "Saving…" : "Save article"}
						</button>
					</>
				}
			>
				{kbsLoaded && kbs.length === 0 ? (
					<p className={styles.modalHint}>
						No knowledge bases yet — create one on the Wiki page first, then save
						artifacts into it.
					</p>
				) : (
					<div className={styles.form}>
						<label className={styles.field}>
							<span className={styles.fieldLabel}>Knowledge base</span>
							<select
								className={styles.select}
								value={saveKb}
								onChange={(e) => setSaveKb(e.target.value)}
							>
								{!kbsLoaded && <option>Loading…</option>}
								{kbs.map((k) => (
									<option key={k.slug} value={k.slug}>
										{k.title}
									</option>
								))}
							</select>
						</label>
						<label className={styles.field}>
							<span className={styles.fieldLabel}>Title</span>
							<input
								className={styles.input}
								type="text"
								value={saveTitle}
								onChange={(e) => setSaveTitle(e.target.value)}
								placeholder="Article title"
							/>
						</label>
						<p className={styles.modalHint}>
							Saved as a wiki article
							{!isPreviewable(language) ? " (content wrapped as a code block)" : ""}.
						</p>
						{saveError && <p className={styles.modalError}>{saveError}</p>}
					</div>
				)}
			</Modal>
		</>
	);
}
